import { useState, useMemo, useEffect } from 'react';
import { CloudType, Alert, AlertType, Severity } from '../types/alerts';
import { CloudSelector, CloudResource, Tenant } from '../components/alerts/CloudSelector';
import { AlertTypeSelector } from '../components/alerts/AlertTypeSelector';
import { SeveritySelector } from '../components/alerts/SeveritySelector';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertDetailsEdit } from '../components/alerts/AlertDetailsEdit';
import { AlertStats } from '../components/alerts/AlertStats';
import { CreateAlertChat } from '../components/alerts/CreateAlertChat';
import { Button } from "../../../ui/common-ui/button";
import { Badge } from '../../../ui/common-ui/badge';
import { Skeleton } from '../../../ui/common-ui/skeleton';
import { Shield, ArrowLeft, Sparkles, PenTool, Loader2, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import { Input } from '../../../ui/common-ui/input';
import { toast } from 'sonner';
import {
  fetchTenants,
  fetchGcpProjectIds,
  fetchAwsAccounts,
  fetchAzureSubscriptions,
  fetchAlerts,
  switchAlert,
  switchAllAlerts,
  TenantData,
  AzureSubscription,
  AwsAccount,
  CloudAlertResponse,
  GroupedAlertsResponse
} from '../services/api';

const transformCloudAlert = (apiAlert: CloudAlertResponse, cloud: CloudType): Alert => {
  const normalizeSeverity = (sev: string): Severity => {
    const s = sev.toLowerCase();
    if (s === 'critical') return 'Critical';
    if (s === 'high') return 'High';
    if (s === 'medium') return 'Medium';
    return 'Low';
  };

  const normalizeAlertType = (type: string): AlertType => {
    const t = type.toLowerCase();
    if (t === 'behaviour' || t === 'behavioral') return 'behaviour';
    if (t === 'sequential') return 'sequential';
    return 'rule';
  };

  const mitre = Object.entries(apiAlert.mitre || {}).map(([code, explanation]) => ({
    code,
    key: code,
    explanation,
  }));

  return {
    id: apiAlert.id,
    alertId: apiAlert.alertId,
    title: apiAlert.title,
    severity: normalizeSeverity(apiAlert.severity),
    type: normalizeAlertType(apiAlert.alertType),
    info: {
      why: apiAlert.info?.why || '',
      what: apiAlert.info?.what || '',
      how: apiAlert.info?.how || '',
    },
    steps: apiAlert.steps,
    mitre,
    isEnabled: !apiAlert.isFlagged,
    isCustom: !apiAlert.isDefault,
    cloud,
    users: apiAlert.users || [],
    resource: apiAlert.resource || [],
    timeWindow: apiAlert.timeWindow || [],
  };
};

const flattenGroupedAlerts = (grouped: GroupedAlertsResponse, cloud: CloudType): Alert[] => {
  const alerts: Alert[] = [];

  for (const alertType of ['behaviour', 'rule', 'sequential'] as const) {
    const byType = grouped[alertType];
    if (!byType) continue;

    for (const severity of ['critical', 'high', 'medium'] as const) {
      const alertList = byType[severity];
      if (!alertList) continue;

      for (const apiAlert of alertList) {
        alerts.push(transformCloudAlert(apiAlert, cloud));
      }
    }
  }

  return alerts;
};

type ViewMode = 'manage' | 'create';
type AlertSource = 'default' | 'custom' | null;

export default function AlertManagement() {
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const [cloudResources, setCloudResources] = useState<Record<CloudType, CloudResource[]>>({
    AWS: [],
    GCP: [],
    AZURE: []
  });

  const [selectedCloud, setSelectedCloud] = useState<CloudType>('GCP');
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [selectedAlertType, setSelectedAlertType] = useState<AlertType | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | null>(null);
  const [selectedSource, setSelectedSource] = useState<AlertSource>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('manage');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [customAlerts, setCustomAlerts] = useState<Alert[]>([]);
  const [isFetchingAlerts, setIsFetchingAlerts] = useState(false);
  const [isTogglingAll, setIsTogglingAll] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const response = await fetchTenants();
        const tenants: Tenant[] = response.map((t: TenantData) => ({ tenantName: t.tenantName }));
        setTenantList(tenants);

        if (tenants.length > 0) {
          const storedTenant = localStorage.getItem('latestTenant');
          let selected: Tenant | null = null;

          if (storedTenant) {
            try {
              const wrapper = JSON.parse(storedTenant);
              const parsed = wrapper.value;
              selected = tenants.find(t => t.tenantName === parsed.tenantName) || null;
            } catch (e) {
              selected = null;
            }
          }

          if (!selected) {
            selected = tenants[0];
          }

          setSelectedTenant(selected);
          localStorage.setItem('latestTenant', JSON.stringify({ value: selected }));
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      }
    };

    loadTenants();
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;

    const loadCloudResources = async () => {
      const tenantName = selectedTenant.tenantName;

      try {
        const [gcpProjects, awsAccounts, azureSubs] = await Promise.all([
          fetchGcpProjectIds(tenantName).catch(() => []),
          fetchAwsAccounts(tenantName).catch(() => []),
          fetchAzureSubscriptions(tenantName).catch(() => [])
        ]);

        const gcpResources: CloudResource[] = gcpProjects.map((projectId: string) => ({
          id: projectId,
          name: projectId
        }));

        const awsResources: CloudResource[] = awsAccounts.map((account: AwsAccount) => ({
          id: account.accountId,
          name: account.accountName
        }));

        const azureResources: CloudResource[] = azureSubs.map((sub: AzureSubscription) => ({
          id: sub.azureSubscriptionId,
          name: sub.subscriptionName
        }));

        const newCloudResources = {
          GCP: gcpResources,
          AWS: awsResources,
          AZURE: azureResources
        };

        setCloudResources(newCloudResources);

        let cloudToSelect: CloudType | null = null;
        let resourceToSelect: CloudResource | null = null;

        const storedGcpProject = localStorage.getItem('gcpSelectedProjectId');
        const storedAwsAccount = localStorage.getItem('selectedAwsAccount');
        const storedAzureSub = localStorage.getItem('latestSubList');

        let gcpProjectId: string | null = null;
        let awsAccountId: string | null = null;
        let azureSubData: { azureSubscriptionId: string; subscriptionName: string } | null = null;

        if (storedGcpProject) {
          try {
            const wrapper = JSON.parse(storedGcpProject);
            gcpProjectId = wrapper.value;
          } catch (e) { }
        }

        if (storedAwsAccount) {
          try {
            const wrapper = JSON.parse(storedAwsAccount);
            awsAccountId = wrapper.value;
          } catch (e) { }
        }

        if (storedAzureSub) {
          try {
            const wrapper = JSON.parse(storedAzureSub);
            azureSubData = typeof wrapper.value === 'string' ? JSON.parse(wrapper.value) : wrapper.value;
          } catch (e) { }
        }

        if (gcpResources.length > 0) {
          const found = gcpProjectId ? gcpResources.find(r => r.id === gcpProjectId) : null;
          if (!cloudToSelect || selectedCloud === 'GCP') {
            cloudToSelect = 'GCP';
            resourceToSelect = found || gcpResources[0];
            localStorage.setItem('gcpSelectedProjectId', JSON.stringify({ value: resourceToSelect.id }));
          }
        }

        if (awsResources.length > 0) {
          const found = awsAccountId ? awsResources.find(r => r.id === awsAccountId) : null;
          if (!cloudToSelect || selectedCloud === 'AWS') {
            cloudToSelect = cloudToSelect || 'AWS';
            if (selectedCloud === 'AWS') {
              resourceToSelect = found || awsResources[0];
              localStorage.setItem('selectedAwsAccount', JSON.stringify({ value: resourceToSelect.id }));
            }
          }
        }

        if (azureResources.length > 0) {
          let found: CloudResource | null = null;
          if (azureSubData) {
            found = azureResources.find(r => r.id === azureSubData!.azureSubscriptionId) || null;
          }
          if (!cloudToSelect || selectedCloud === 'AZURE') {
            cloudToSelect = cloudToSelect || 'AZURE';
            if (selectedCloud === 'AZURE') {
              resourceToSelect = found || azureResources[0];
              const subData = JSON.stringify({
                azureSubscriptionId: resourceToSelect.id,
                subscriptionName: resourceToSelect.name
              });
              localStorage.setItem('latestSubList', JSON.stringify({ value: subData }));
            }
          }
        }

        if (cloudToSelect) {
          setSelectedCloud(cloudToSelect);
          const cloudRes = newCloudResources[cloudToSelect];
          if (cloudRes.length > 0) {
            resourceToSelect = resourceToSelect || cloudRes[0];
            setSelectedResource(resourceToSelect);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cloud resources:', error);
      }
    };

    loadCloudResources();
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedResource) {
      fetchAlertsForResource(selectedCloud, selectedResource);
    }
  }, [selectedCloud, selectedResource]);

  const fetchAlertsForResource = async (cloud: CloudType, resource: CloudResource) => {
    if (!selectedTenant) return;

    setIsFetchingAlerts(true);
    try {
      const groupedAlerts = await fetchAlerts(
        selectedTenant.tenantName,
        resource.id,
        cloud
      );
      const transformedAlerts = flattenGroupedAlerts(groupedAlerts, cloud);
      setAlerts(transformedAlerts);
      toast.success(`Loaded ${transformedAlerts.length} alerts for ${resource.name}`);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to fetch alerts');
      setAlerts([]);
    } finally {
      setIsFetchingAlerts(false);
    }
  };

  const handleCloudChange = (cloud: CloudType) => {
    const resources = cloudResources[cloud];
    if (resources.length === 0) return;

    setSelectedCloud(cloud);

    let resourceToSelect: CloudResource | null = null;

    if (cloud === 'GCP') {
      const stored = localStorage.getItem('gcpSelectedProjectId');
      let storedId: string | null = null;
      if (stored) {
        try {
          const wrapper = JSON.parse(stored);
          storedId = wrapper.value;
        } catch (e) { }
      }
      resourceToSelect = (storedId ? resources.find(r => r.id === storedId) : null) || resources[0];
      localStorage.setItem('gcpSelectedProjectId', JSON.stringify({ value: resourceToSelect.id }));
    } else if (cloud === 'AWS') {
      const stored = localStorage.getItem('selectedAwsAccount');
      let storedId: string | null = null;
      if (stored) {
        try {
          const wrapper = JSON.parse(stored);
          storedId = wrapper.value;
        } catch (e) { }
      }
      resourceToSelect = (storedId ? resources.find(r => r.id === storedId) : null) || resources[0];
      localStorage.setItem('selectedAwsAccount', JSON.stringify({ value: resourceToSelect.id }));
    } else if (cloud === 'AZURE') {
      const stored = localStorage.getItem('latestSubList');
      if (stored) {
        try {
          const wrapper = JSON.parse(stored);
          const parsed = typeof wrapper.value === 'string' ? JSON.parse(wrapper.value) : wrapper.value;
          resourceToSelect = resources.find(r => r.id === parsed.azureSubscriptionId) || resources[0];
        } catch (e) {
          resourceToSelect = resources[0];
        }
      } else {
        resourceToSelect = resources[0];
      }
      const subData = JSON.stringify({
        azureSubscriptionId: resourceToSelect.id,
        subscriptionName: resourceToSelect.name
      });
      localStorage.setItem('latestSubList', JSON.stringify({ value: subData }));
    }

    setSelectedResource(resourceToSelect);
  };

  const handleResourceChange = (cloud: CloudType, resource: CloudResource) => {
    setSelectedResource(resource);

    if (cloud === 'GCP') {
      localStorage.setItem('gcpSelectedProjectId', JSON.stringify({ value: resource.id }));
    } else if (cloud === 'AWS') {
      localStorage.setItem('selectedAwsAccount', JSON.stringify({ value: resource.id }));
    } else if (cloud === 'AZURE') {
      const subData = JSON.stringify({
        azureSubscriptionId: resource.id,
        subscriptionName: resource.name
      });
      localStorage.setItem('latestSubList', JSON.stringify({ value: subData }));
    }
  };

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    localStorage.setItem('latestTenant', JSON.stringify({ value: tenant }));
    setCloudResources({ AWS: [], GCP: [], AZURE: [] });
    setSelectedResource(null);
    toast.success(`Switched to ${tenant.tenantName}`);
  };

  const handleToggleAlert = async (id: number, enabled: boolean) => {
    const alert = [...alerts, ...customAlerts].find(a => a.id === id);
    if (!alert || !alert.alertId) {
      toast.error('Cannot toggle alert: missing alert ID');
      return;
    }
    if (!selectedTenant || !selectedResource) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    try {
      const success = await switchAlert(
        alert.alertId,
        selectedCloud,
        selectedTenant.tenantName,
        selectedResource.id
      );

      if (success) {
        setAlerts(prev =>
          prev.map(a =>
            a.id === id ? { ...a, isEnabled: enabled } : a
          )
        );
        setCustomAlerts(prev =>
          prev.map(a =>
            a.id === id ? { ...a, isEnabled: enabled } : a
          )
        );
        toast.success(enabled ? 'Alert enabled' : 'Alert disabled');
      } else {
        toast.error('Failed to toggle alert');
      }
    } catch (error) {
      console.error('Failed to switch alert:', error);
      toast.error('Failed to toggle alert');
    }
  };

  const handleViewDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setDetailsOpen(true);
  };

  const handleAlertsAdded = (newAlerts: Alert[]) => {
    setCustomAlerts(prev => [...newAlerts, ...prev]);
  };

  const handleToggleAllAlerts = async (enableAll: boolean) => {
    if (!selectedTenant || !selectedResource) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    setIsTogglingAll(true);
    try {
      const isFlagged = !enableAll;

      const success = await switchAllAlerts(
        selectedTenant.tenantName,
        selectedResource.id,
        selectedCloud,
        isFlagged
      );

      if (success) {
        setAlerts(prev => prev.map(a => ({ ...a, isEnabled: enableAll })));
        setCustomAlerts(prev => prev.map(a =>
          a.cloud === selectedCloud ? { ...a, isEnabled: enableAll } : a
        ));
        toast.success(enableAll ? 'All alerts enabled' : 'All alerts disabled');
      } else {
        toast.error('Failed to toggle all alerts');
      }
    } catch (error) {
      console.error('Failed to toggle all alerts:', error);
      toast.error('Failed to toggle all alerts');
    } finally {
      setIsTogglingAll(false);
    }
  };

  const allAlerts = useMemo(() => {
    const alertIds = new Set(alerts.map(a => a.alertId));
    const uniqueCustomAlerts = customAlerts.filter(ca => ca.cloud === selectedCloud && !alertIds.has(ca.alertId));
    return [...uniqueCustomAlerts, ...alerts];
  }, [alerts, customAlerts, selectedCloud]);

  const customAlertCount = useMemo(() => {
    return allAlerts.filter(a => a.isCustom === true).length;
  }, [allAlerts]);

  const sortedAlerts = useMemo(() => {
    const severityOrder: Record<Severity, number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3
    };

    let filtered = allAlerts;

    if (selectedSource === 'custom') {
      filtered = filtered.filter(a => a.isCustom === true);
    } else if (selectedSource === 'default') {
      filtered = filtered.filter(a => !a.isCustom);
    }

    if (selectedAlertType !== null) {
      filtered = filtered.filter(a => a.type === selectedAlertType);
    }

    if (selectedSeverity !== null) {
      filtered = filtered.filter(a => a.severity === selectedSeverity);
    }

    if (searchQuery.trim()) {
      const queryTokens = searchQuery
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      filtered = filtered.filter((a) => {
        const titleWords = a.title.toLowerCase().split(/\s+/).filter(Boolean);
        for (let i = 0; i <= titleWords.length - queryTokens.length; i++) {
          let ok = true;
          for (let j = 0; j < queryTokens.length; j++) {
            if (!titleWords[i + j].startsWith(queryTokens[j])) {
              ok = false;
              break;
            }
          }
          if (ok) return true;
        }
        return false;
      });
    }

    return [...filtered].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [allAlerts, selectedAlertType, selectedSeverity, selectedSource, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center relative">
            {viewMode === 'create' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('manage')}
                className="text-muted-foreground hover:text-foreground absolute left-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div className="text-center">
                <h1 className="text-xl font-bold text-foreground">
                  {viewMode === 'manage' ? 'Alert Management' : 'Create Custom Alert'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {viewMode === 'manage'
                    ? 'Configure and manage cloud security alerts'
                    : 'Generate custom detection rules with AI'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {viewMode === 'manage' && (
          <>
            <div className="mb-6">
              <AlertStats alerts={allAlerts} />
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <CloudSelector
                  tenantList={tenantList}
                  selectedTenant={selectedTenant}
                  selectedCloud={selectedCloud}
                  selectedResource={selectedResource}
                  cloudResources={cloudResources}
                  onTenantChange={handleTenantChange}
                  onCloudChange={handleCloudChange}
                  onResourceChange={handleResourceChange}
                  isFetchingAlerts={isFetchingAlerts}
                />
                <Button
                  onClick={() => setViewMode('create')}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 px-6"
                >
                  <Sparkles className="h-5 w-5" />
                  Create Alert
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground w-14">Source:</span>
                  <div className="flex items-center gap-0.5 p-1 bg-card rounded-lg border border-border shadow-sm h-9">
                    <button
                      onClick={() => setSelectedSource(selectedSource === 'default' ? null : 'default')}
                      className={`flex items-center justify-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-all min-w-[70px] h-7 ${selectedSource === 'default' ? 'bg-background text-foreground shadow-md ring-1 ring-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-background/60'}`}
                    >
                      <Shield className="h-3.5 w-3.5" />
                      Default
                    </button>
                    <button
                      onClick={() => setSelectedSource(selectedSource === 'custom' ? null : 'custom')}
                      className={`flex items-center justify-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-all min-w-[70px] h-7 ${selectedSource === 'custom' ? 'bg-background text-foreground shadow-md ring-1 ring-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-background/60'}`}
                    >
                      <PenTool className="h-3.5 w-3.5" />
                      Custom
                      {customAlertCount > 0 && (
                        <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5">
                          {customAlertCount}
                        </Badge>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Type:</span>
                  <AlertTypeSelector
                    selectedType={selectedAlertType}
                    onTypeChange={(type) => {
                      if (type !== 'automatic') {
                        setSelectedAlertType(type as AlertType | null);
                      }
                    }}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Severity:</span>
                  <SeveritySelector
                    selectedSeverity={selectedSeverity}
                    onSeverityChange={setSelectedSeverity}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search alerts by title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 bg-card border-border"
                  />
                </div>
                <Button
                  onClick={() => handleToggleAllAlerts(true)}
                  disabled={isTogglingAll || sortedAlerts.length === 0}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-10 whitespace-nowrap"
                >
                  {isTogglingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ToggleRight className="h-4 w-4" />
                  )}
                  Enable All
                </Button>
                <Button
                  onClick={() => handleToggleAllAlerts(false)}
                  disabled={isTogglingAll || sortedAlerts.length === 0}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-10 whitespace-nowrap"
                >
                  {isTogglingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ToggleLeft className="h-4 w-4" />
                  )}
                  Disable All
                </Button>
              </div>
            </div>
          </>
        )}

        {viewMode === 'create' && (
          <>
            <div className="mb-6">
              <CloudSelector
                tenantList={tenantList}
                selectedTenant={selectedTenant}
                selectedCloud={selectedCloud}
                selectedResource={selectedResource}
                cloudResources={cloudResources}
                onTenantChange={handleTenantChange}
                onCloudChange={handleCloudChange}
                onResourceChange={handleResourceChange}
                isFetchingAlerts={isFetchingAlerts}
              />
            </div>
            <CreateAlertChat
              selectedCloud={selectedCloud}
              tenantName={selectedTenant?.tenantName || ''}
              cloudId={selectedResource?.id || ''}
              onAlertsAdded={handleAlertsAdded}
              onRefreshAlerts={() => {
                if (selectedResource) {
                  fetchAlertsForResource(selectedCloud, selectedResource);
                }
              }}
            />
          </>
        )}

        {isFetchingAlerts && viewMode === 'manage' && sortedAlerts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex justify-between pt-2">
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-8 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isFetchingAlerts && viewMode === 'manage' && sortedAlerts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <Loader2 className="h-6 w-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
            <p className="text-muted-foreground text-sm">Loading alerts...</p>
          </div>
        )}

        {!isFetchingAlerts && !sortedAlerts.length && viewMode === 'manage' && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              No alerts found with the selected filters
            </p>
          </div>
        )}

        {!isFetchingAlerts && sortedAlerts.length > 0 && viewMode === 'manage' && (
          <div
            key={`${selectedAlertType}-${selectedSeverity}-${selectedSource}-${searchQuery}`}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {sortedAlerts.map((alert, index) => (
              <div
                key={`${alert.id}-${selectedAlertType}-${selectedSeverity}-${selectedSource}-${searchQuery}`}
                className="animate-scale-in"
                style={{
                  animationDelay: `${index * 40}ms`,
                  animationFillMode: 'forwards',
                  animationDuration: '0.3s'
                }}
              >
                <AlertCard
                  alert={alert}
                  onToggle={handleToggleAlert}
                  onViewDetails={handleViewDetails}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDetailsEdit
        alert={selectedAlert}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        tenantName={selectedTenant?.tenantName || ''}
        cloudId={selectedResource?.id || ''}
        selectedCloud={selectedCloud}
        onSaveComplete={() => {
          if (selectedResource) {
            fetchAlertsForResource(selectedCloud, selectedResource);
          }
        }}
      />
    </div>
  );
}